import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking
    const [ready, setReady] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem("fcr_token");
        if (!token) {
            setUser(false);
            setReady(true);
            return;
        }
        api
            .get("/auth/me")
            .then((res) => setUser(res.data))
            .catch(() => {
                localStorage.removeItem("fcr_token");
                setUser(false);
            })
            .finally(() => setReady(true));
    }, []);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("fcr_token", data.access_token);
            setUser(data.user);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    }, []);

    const register = useCallback(async (email, password, name) => {
        try {
            const { data } = await api.post("/auth/register", { email, password, name });
            localStorage.setItem("fcr_token", data.access_token);
            setUser(data.user);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem("fcr_token");
        setUser(false);
    }, []);

    const value = useMemo(
        () => ({ user, ready, login, register, logout }),
        [user, ready, login, register, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
